import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { useMemo, useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { useIntl } from 'react-intl';

import { generateUploadUrls } from 'apis/mips/api';
import { UploadImageUrlData } from 'apis/mips/types';
import { getErrorContent } from 'common/utils';

import { PhotoShootQueryKeys } from '../constants/queryKeys';
import { PhotoShootModel } from '../types';

type FileToUpload = { file: File; uploadUrl: string };
type UploadProgress = { count: number; total: number };
type UseImagesUploadMutationArgs = {
  onUploadFinished: (images: PhotoShootModel['images']) => void;
  venueId: string;
  token: string | null;
};

const getFilesToUpload = (uploadUrlsData: UploadImageUrlData[], files: File[]): FileToUpload[] =>
  uploadUrlsData.reduce((acc, uploadUrlData) => {
    const file = files.find(file => file.name === uploadUrlData.fileName);
    if (!file) {
      return acc;
    }

    return [
      ...acc,
      {
        file,
        uploadUrl: uploadUrlData.url,
      },
    ];
  }, []);

const INITIAL_PROGRESS: UploadProgress = {
  count: 0,
  total: 0,
};
const MAX_CONCURRENT_UPLOADS = 6;
const RETRY_COUNT = 3;

export const useImagesUploadMutation = ({ venueId, token, onUploadFinished }: UseImagesUploadMutationArgs) => {
  const [notUploadedFilesList, setNotUploadedFilesList] = useState<string[]>([]);
  const [progress, setUploadProgress] = useState<UploadProgress>(INITIAL_PROGRESS);
  const queryClient = useQueryClient();
  const intl = useIntl();

  const { mutateAsync: handleUploadToS3, isLoading: isUploadingToS3 } = useMutation(
    (file: FileToUpload) => axios.put(file.uploadUrl, file.file),
    {
      retry: RETRY_COUNT,
      onSuccess: () => setUploadProgress(prev => ({ ...prev, count: prev.count + 1 })),
      onError: (_, file) => {
        setNotUploadedFilesList(prev => [...prev, file.file.name]);
      },
    },
  );

  const {
    mutate: handleUploadUrlsGeneration,
    isLoading: isGeneratingUploadUrls,
    error: uploadUrlsGenerationError,
  } = useMutation(
    async ({ acceptedFiles }: { acceptedFiles: File[]; fileRejections: FileRejection[] }) => {
      if (acceptedFiles.length === 0) {
        return;
      }

      const filesNames = acceptedFiles.map(file => file.name);
      return generateUploadUrls(venueId, filesNames, token);
    },
    {
      retry: RETRY_COUNT,
      onSuccess: (data, { acceptedFiles }) => {
        if (!data) {
          return;
        }

        setUploadProgress({ ...INITIAL_PROGRESS, total: acceptedFiles.length });

        const files = getFilesToUpload(data.uploadUrls, acceptedFiles);

        const chainUploadFile = (file: FileToUpload): Promise<AxiosResponse> =>
          handleUploadToS3(file).finally(() => {
            const nextFile = files.pop();

            if (!nextFile) {
              return;
            }

            return chainUploadFile(nextFile);
          });

        const firstFileBatchToUpload = files.splice(0, MAX_CONCURRENT_UPLOADS);
        const filesUploadPromises = firstFileBatchToUpload.map(file => chainUploadFile(file));

        return Promise.allSettled(filesUploadPromises).finally(async () => {
          setUploadProgress(INITIAL_PROGRESS);
          await queryClient.invalidateQueries(PhotoShootQueryKeys.GET_PHOTO_SHOOT(venueId, token));

          const updatedData = queryClient.getQueryData<PhotoShootModel>(
            PhotoShootQueryKeys.GET_PHOTO_SHOOT(venueId, token),
          );

          setTimeout(() => onUploadFinished(updatedData?.images || []), 0);
        });
      },
    },
  );

  const errorMessage = useMemo(() => {
    if (notUploadedFilesList.length > 0) {
      return intl.formatMessage(
        { id: 'photo-shoot.upload-error-cannot-upload-photos' },
        { photoNames: notUploadedFilesList.join(', ') },
      );
    }

    if (uploadUrlsGenerationError) {
      return getErrorContent(uploadUrlsGenerationError) || intl.formatMessage({ id: 'photo-shoot.upload-errors' });
    }

    return;
  }, [intl, uploadUrlsGenerationError, notUploadedFilesList]);

  return {
    handlePhotosUpload: handleUploadUrlsGeneration,
    isLoading: isGeneratingUploadUrls || isUploadingToS3,
    progress,
    errorMessage,
  };
};
